import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface PlatformUpdate {
  id: string;
  title: string;
  body: string;
  emoji: string;
  requires_consent: boolean;
  published_at: string;
}

/**
 * Pop-up de notas de atualização: mostra as novidades da plataforma que o
 * usuário ainda não viu. Atualizações comuns fecham com "Entendi!"; as que
 * envolvem dados sensíveis exigem consentimento explícito (checkbox).
 */
export function UpdateNotesModal() {
  const { user } = useAuth();
  const [queue, setQueue] = useState<PlatformUpdate[]>([]);
  const [consented, setConsented] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      // Só novidades publicadas depois que o usuário se cadastrou — evita
      // "spammar" quem acabou de entrar com o histórico inteiro de releases.
      let updatesQuery = supabase
        .from('platform_updates')
        .select('*')
        .order('published_at', { ascending: true });
      if (user.created_at) {
        updatesQuery = updatesQuery.gt('published_at', user.created_at);
      }
      const [updatesRes, acksRes] = await Promise.all([
        updatesQuery,
        supabase.from('platform_update_acks').select('update_id').eq('user_id', user.id),
      ]);
      const acked = new Set(((acksRes.data ?? []) as { update_id: string }[]).map((a) => a.update_id));
      const pending = ((updatesRes.data ?? []) as PlatformUpdate[]).filter((u) => !acked.has(u.id));
      setQueue(pending);
    })();
  }, [user]);

  const current = queue[0];
  if (!user || !current) return null;

  async function ack() {
    if (!current || !user) return;
    setBusy(true);
    await supabase.from('platform_update_acks').insert({
      update_id: current.id,
      user_id: user.id,
      consented: current.requires_consent ? consented : false,
    } as never);
    setQueue((prev) => prev.slice(1));
    setConsented(false);
    setBusy(false);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="card w-full max-w-md p-6 space-y-4 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center text-3xl">
          {current.emoji || '🎉'}
        </div>
        <h3 className="font-display font-bold text-xl text-ink flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5 text-brand-700" />
          {current.title}
        </h3>
        <p className="text-sm text-ink-muted whitespace-pre-wrap text-left">{current.body}</p>

        {current.requires_consent ? (
          <>
            <label className="flex items-start gap-2 text-xs text-ink text-left cursor-pointer rounded-xl bg-surface-subtle px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
              />
              Li e concordo com as mudanças descritas acima, incluindo o tratamento
              dos novos dados conforme a Política de Privacidade.
            </label>
            <button
              onClick={() => void ack()}
              disabled={busy || !consented}
              className="btn-primary w-full"
            >
              Concordar e continuar
            </button>
          </>
        ) : (
          <button onClick={() => void ack()} disabled={busy} className="btn-primary w-full text-base">
            Entendi! 🚀
          </button>
        )}

        {queue.length > 1 && (
          <p className="text-[11px] text-ink-soft">+{queue.length - 1} novidade(s) na fila</p>
        )}
      </div>
    </div>
  );
}
